// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import LinesHeader, { LinesHeaderStyles } from "@guestsupply/App/Components/Cart/LinesHeader";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

interface OwnProps {
    shipmentGroupingInfo: string;
}

export interface CartLinesStyles {
    headerWrapper?: InjectableCss;
    orderLinesTypography?: TypographyProps;
    tooltipStyle?: TooltipPresentationProps;
    freeShippingLink?: LinkPresentationProps;
    lineHeaderStyle?: LinesHeaderStyles;
}

export const cartLinesStyles: CartLinesStyles = {};

const styles = cartLinesStyles;

const StyledSection = getStyledWrapper("section");

const CartLinesHeader: FC<OwnProps> = ({ shipmentGroupingInfo }) => {
    const { isCheckoutReviewAndSubmitPage, isOrderConfirmationPage, isAuthenticated } = useAppSelector(state => ({
        isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
        isOrderConfirmationPage: getCurrentPage(state).type === "OrderConfirmationPage",
        isAuthenticated: xIsAuthenticated(state),
    }));

    const orderLinesVariant = isOrderConfirmationPage || isCheckoutReviewAndSubmitPage ? "h4" : "h3";

    let showGroups = false;
    const shipmentGroupInfo = JSON.parse(shipmentGroupingInfo);
    if (shipmentGroupInfo?.groups?.length) {
        showGroups = true;
    }

    return (
        <>
            {showGroups ? (
                <StyledSection {...styles.headerWrapper} {...{ isOrderConfirmationPage }} id="cartHeader">
                    <Typography {...styles.orderLinesTypography} variant={orderLinesVariant}>
                        {translate("Order Lines")}
                    </Typography>
                    {isAuthenticated && (
                        <Tooltip
                            {...styles.tooltipStyle}
                            text={
                                siteMessage(
                                    "Guest_Free_Shipping_Notice_Message_Msg",
                                    "Please note that the shipping minimum noted below is for stocked items. Drop ship items will always incur shipping & handling fees and the subtotal of those items will not be calculated towards your freight minimum.",
                                ) as string
                            }
                            triggerComponent={
                                <Link
                                    {...styles.freeShippingLink}
                                    data-test-selector="totalFreightCharges"
                                    aria-label="free shipping on eligible items"
                                >
                                    {translate("free shipping on eligible items")}
                                </Link>
                            }
                        />
                    )}
                </StyledSection>
            ) : (
                <LinesHeader extendedStyles={styles.lineHeaderStyle} />
            )}
        </>
    );
};

export default CartLinesHeader;
